import { Http, PageQuery, ListResponse } from '@mygpt/app';
import { MessageDetailInfo } from '@mygpt/types';

export interface MessagesListParams extends PageQuery {
  aiId: string;
  start_time?: string;
  end_time?: string;
}

export interface MessageHistoryParams extends MessagesListParams {
  /** 查询关键字 */
  keyword?: string;
  /** 来源，可选值: widget(组件), api(接口), playground(调试), share(分享), realtime(实时对话) */
  source?: 'widget' | 'api' | 'playground' | 'share' | 'realtime';
  /** 查询语言，如 Chinese, Japanese 等 */
  language?: string;
  /** Feedback 类型，可选值: good(好评), bad(差评) */
  feedback_type?: 'good' | 'bad';
  /** 是否包含测试数据 */
  include_test?: boolean;
  /** 用户标识 */
  user_identifier?: string;
  tags?: string[];
  /** 来源页面 URL 模糊匹配（大小写不敏感子串），用于区分同一 Bot 嵌入的不同站点 */
  url_keyword?: string;
}

export interface SessionMessagesListParams extends MessagesListParams {
  rating?: string;
  only_rating?: boolean;
  unanswerable?: boolean;
  user_identifier?: string;
}

interface SessionMessagesListRsp extends ListResponse {
  items: MessageDetailInfo[];
}

export interface ExportUnknownAnswersParams {
  aiId: string;
  start_time: string;
  end_time: string;
}

interface unknownAnswersItem {
  answer: string;
  created_at: string;
  faq_id: string | null;
  id: string;
  llm_can_answer: boolean;
  question: string;
  session__id: string;
}

/**
 * 获取点赞列表
 * @param params
 * @returns
 */
export const sessionMessagesList = (params: SessionMessagesListParams) => {
  const { aiId, end_time, only_rating, page, rating, size, start_time, unanswerable, user_identifier } = params;
  const { post } = Http.use();

  const urlQuery = {
    only_rating,
    rating,
    start_time,
    end_time,
    page,
    size,
    unanswerable,
    user_identifier,
  } as { [key: string]: string | boolean | number };

  const queryParams = new URLSearchParams();
  for (const key in urlQuery) {
    if (urlQuery[key]) {
      queryParams.append(key, String(urlQuery[key]));
    }
  }

  return post<SessionMessagesListRsp>(`/questions/${aiId}/session.messages.list?${queryParams.toString()}`);
};

/**
 * 获取消息历史列表
 * @param params
 * @returns
 */
export const messagesHistoryList = (params: MessageHistoryParams) => {
  const {
    aiId,
    end_time,
    page,
    size,
    start_time,
    keyword,
    source,
    language,
    feedback_type,
    include_test,
    user_identifier,
    tags,
    url_keyword,
  } = params;
  const { post } = Http.use();

  const urlQuery = {
    start_time,
    end_time,
    page,
    size,
    keyword,
    source,
    language,
    feedback_type,
    include_test: include_test?.toString(),
    user_identifier,
    tags,
    url_keyword,
  } as { [key: string]: string | number | undefined };

  const queryParams = new URLSearchParams();
  for (const key in urlQuery) {
    if (urlQuery[key]) {
      queryParams.append(key, String(urlQuery[key]));
    }
  }

  return post<SessionMessagesListRsp>(`/questions/${aiId}/session.messages.history.list?${queryParams.toString()}`);
};

/**
 * 导出回答"不知道"的问题
 */
export const exportUnknownAnswers = (params: ExportUnknownAnswersParams) => {
  const { aiId, end_time, start_time } = params;
  const timeParams = start_time && end_time ? `?start_time=${start_time}&end_time=${end_time}` : '';

  const { get } = Http.use();
  return get<unknownAnswersItem[]>(`/session/metrics/${aiId}/export/unknown_answers${timeParams}`);
};

export interface GetMetricsParamsChat {
  aiId: string;
  start_time: string;
  end_time: string;
}

/**
 * 查询打标统计信息
 * @param params
 * @returns
 */
export const getSessionMetrics = (params: GetMetricsParamsChat) => {
  const { aiId, end_time, start_time } = params;
  const { post } = Http.use();
  return post(
    `/questions/${aiId}/session/group_by_day?start_time=${encodeURIComponent(start_time)}&end_time=${encodeURIComponent(
      end_time
    )}&size=1000`
  );
};
export const getMessageMetrics = (params: GetMetricsParamsChat) => {
  const { aiId, end_time, start_time } = params;
  const { post } = Http.use();
  return post(
    `/questions/${aiId}/session/messages/group_by_day?start_time=${encodeURIComponent(
      start_time
    )}&end_time=${encodeURIComponent(end_time)}&size=1000`
  );
};

export interface EvaluateSessionParams {
  aiId: string;
  sessionId: string;
  score: number | string;
}

/**
 * 会话评分
 * @param params
 * @returns
 */
export const evaluateSession = (params: EvaluateSessionParams) => {
  const { aiId, score, sessionId } = params;
  const { put } = Http.use();
  return put(`/questions/${aiId}/session/${sessionId}/evaluate`, score);
};
